import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowClockwise, ArrowLeft, ArrowRight, ClipboardText, WarningCircle } from 'shared/icons';
import api from '../../../services/api';
import { FormatInt } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import { BigAction, NoticeBanner } from './CollectorChrome';
import { PlayScanError, PlayScanSuccess } from './collectorFeedback';
import {
    IsServiceOrderTakenError,
    ParseOperatorBusy,
    ParseServiceOrder,
    ParseServiceOrderList,
    SERVICE_ORDER_STATUS_PAUSED,
    SplitCollectorQueue,
} from './collectorServiceOrders';

/**
 * A FILA DO CHÃO — as OSs liberadas + a que já está com este crachá.
 *
 * ── O aceite é uma CORRIDA, e a tela sabe disso ─────────────────────────────
 * O Supervisor designa a OS verbalmente ("pega a zero-zero-três") e todo mundo
 * vê a mesma lista: dois operadores tocando "Aceitar" na mesma OS é rotina, não
 * exceção. A trava é do banco (first-wins) e quem perde recebe 409 — que aqui
 * NÃO é tela de erro: é um aviso de uma linha ("outro operador aceitou esta OS
 * primeiro") e a lista se recarrega sozinha, porque a informação que o perdedor
 * precisa é "o que sobrou para mim", não um pedido de desculpas.
 *
 * ── 1 OS ativa por operador ─────────────────────────────────────────────────
 * Tentar aceitar com uma OS na mão também é 409, e a resposta diz QUAL. Em vez
 * de só negar, a tela oferece o caminho: um botão que abre a OS ativa (onde
 * está o "Pausar"). Negar sem mostrar a saída é o que faz o operador chamar o
 * Supervisor por algo que ele resolveria sozinho em dois toques.
 *
 * ── Por que a lista se atualiza sozinha ─────────────────────────────────────
 * A fila muda do lado do Supervisor, não do lado do coletor. Um operador
 * parado numa lista vazia esperando trabalho não deve precisar aprender a
 * puxar para atualizar — mas o botão existe do mesmo jeito, porque quando a
 * rede oscila o toque manual é a única coisa que dá sensação de controle. Se a
 * carga falha, o ciclo automático PARA: insistir contra um backend fora do ar
 * só empilha erro na tela.
 */

const AUTO_REFRESH_MS = 20000;
const QUEUE_PAGE_SIZE = 100;

/**
 * O cabeçalho de um cartão de OS: código grande e monoespaçado (é o que o
 * Supervisor FALA — "pega a zero-zero-três"), tipo, contagem de itens e o aviso
 * de par. O par aparece aqui e não só na execução porque saber que existe uma
 * OS irmã muda o que o operador espera do próprio trabalho: em algum ponto ele
 * vai ter de esperar o colega, e isso não pode ser surpresa no meio da rota.
 */
const ServiceOrderHead = ({ label, order }) => {
    const total = Number(order?.items_count ?? 0);
    const done = Number(order?.items_done_count ?? 0) + Number(order?.items_skipped_count ?? 0);

    return (
        <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
                <Mono className="text-2xl font-black text-black dark:text-white">{order?.code || '—'}</Mono>
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                    {order?.status ? (
                        <Badge variant={order.status === SERVICE_ORDER_STATUS_PAUSED ? 'warning' : 'info'}>
                            {label(`wms.service_order.status.${order.status}`)}
                        </Badge>
                    ) : null}
                    {order?.group_id ? <Badge variant="default">{label('wms.service_order.field.group')}</Badge> : null}
                </div>
            </div>
            <p className="text-base font-semibold text-black dark:text-white">
                {order?.type_label || order?.type_code || '—'}
            </p>
            <p className="text-sm text-mono-600 dark:text-mono-400">
                {`${label('wms.service_order.field.items')}: ${FormatInt(done)}/${FormatInt(total)}`}
            </p>
            {/* O equipamento da OS é uma instrução de véspera: quem lê "PJ-01"
                antes de aceitar sabe que precisa buscar a transpaleteira, em vez
                de descobrir isso na frente do primeiro pallet. */}
            {order?.equipment_code ? (
                <p className="text-sm text-mono-600 dark:text-mono-400">
                    {`${label('wms.service_order.field.equipment')}: `}
                    <Mono className="font-semibold text-black dark:text-white">{order.equipment_code}</Mono>
                </p>
            ) : null}
        </div>
    );
};

const ServiceOrderList = ({ label, operator, onOpen, onExit }) => {
    const toast = useToast();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [acceptingId, setAcceptingId] = useState(null);
    const [notice, setNotice] = useState(null);
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const operatorCode = operator?.employee_code || null;

    const load = useCallback(
        async ({ quiet = false } = {}) => {
            if (!quiet) {
                setLoading(true);
            }
            try {
                const payload = await api.getWarehouseServiceOrders({
                    operator_code: operatorCode || undefined,
                    limit: QUEUE_PAGE_SIZE,
                });
                if (!aliveRef.current) {
                    return;
                }
                setOrders(ParseServiceOrderList(payload));
                setLoadError(null);
            } catch (error) {
                if (!aliveRef.current) {
                    return;
                }
                setLoadError(error?.message || label('wms.common.load_error'));
            } finally {
                if (aliveRef.current) {
                    setLoading(false);
                }
            }
        },
        [label, operatorCode],
    );

    useEffect(() => {
        load();
    }, [load]);

    // Ciclo automático — só enquanto a última carga deu certo e ninguém está no
    // meio de um aceite (recarregar debaixo do dedo do operador troca a lista
    // entre o toque e a resposta).
    useEffect(() => {
        if (loadError || acceptingId) {
            return undefined;
        }
        const timer = window.setInterval(() => load({ quiet: true }), AUTO_REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [acceptingId, load, loadError]);

    const { active, ready } = SplitCollectorQueue(orders, operatorCode);

    const handleAccept = useCallback(
        async (order) => {
            setAcceptingId(order.id);
            setNotice(null);
            try {
                const payload = await api.acceptWarehouseServiceOrder(order.id, operatorCode);
                PlayScanSuccess();
                // A resposta do aceite JÁ é a ficha completa — abrir com ela
                // evita uma segunda ida ao servidor antes do primeiro bipe.
                onOpen(ParseServiceOrder(payload) || order);
            } catch (error) {
                if (IsServiceOrderTakenError(error)) {
                    PlayScanError();
                    setNotice({ tone: 'warning', text: label('wms.collector.accept_taken') });
                    load({ quiet: true });
                    return;
                }
                const busy = ParseOperatorBusy(error);
                if (busy) {
                    PlayScanError();
                    setNotice({ tone: 'warning', text: label('wms.collector.already_active'), busy });
                    load({ quiet: true });
                    return;
                }
                PlayScanError();
                toast.error(error);
            } finally {
                if (aliveRef.current) {
                    setAcceptingId(null);
                }
            }
        },
        [label, load, onOpen, operatorCode, toast],
    );

    const noticeAction = notice?.busy ? (
        <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() =>
                onOpen(
                    orders.find((row) => row.id === notice.busy.id) || {
                        id: notice.busy.id,
                        code: notice.busy.code,
                        status: notice.busy.status,
                    },
                )
            }
        >
            {label('wms.collector.open_active_service_order')}
            <ArrowRight size={20} className="ml-2" />
        </Button>
    ) : null;

    return (
        <div className="flex min-h-[70vh] flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                    <h2 className="truncate text-xl font-bold text-black dark:text-white">
                        {label('wms.collector.service_orders_title')}
                    </h2>
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        {label('wms.collector.service_orders_hint')}
                    </p>
                </div>
                <Button
                    variant="secondary"
                    size="md"
                    onClick={() => load()}
                    loading={loading}
                    aria-label={label('wms.collector.service_orders_refresh')}
                >
                    <ArrowClockwise size={22} />
                </Button>
            </div>

            {notice ? (
                <NoticeBanner tone={notice.tone} action={noticeAction}>
                    {notice.text}
                </NoticeBanner>
            ) : null}

            <div className="flex-1 space-y-4">
                {/* A MINHA OS primeiro: quem já tem trabalho na mão não está aqui
                    para escolher, está para voltar ao que estava fazendo. */}
                {active ? (
                    <section className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.collector.my_service_order')}
                        </p>
                        <div className="space-y-3 rounded-2xl border-2 border-black bg-white p-4 dark:border-white dark:bg-mono-950">
                            <ServiceOrderHead label={label} order={active} />
                            <BigAction onClick={() => onOpen(active)}>
                                {active.status === SERVICE_ORDER_STATUS_PAUSED
                                    ? label('wms.pause.resume')
                                    : label('wms.collector.continue_service_order')}
                            </BigAction>
                        </div>
                    </section>
                ) : null}

                <section className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label('wms.kanban.READY')}
                    </p>

                    {loadError ? (
                        <div className="space-y-3 rounded-2xl border-2 border-danger-300 bg-danger-soft p-4 text-center dark:border-danger-800 dark:bg-danger-900/30">
                            <WarningCircle size={40} className="mx-auto text-danger-text dark:text-danger-300" />
                            <p className="text-sm font-medium text-danger-text dark:text-danger-200">{loadError}</p>
                            <Button variant="secondary" size="lg" className="w-full" onClick={() => load()}>
                                {label('wms.common.retry')}
                            </Button>
                        </div>
                    ) : ready.length === 0 ? (
                        <div className="rounded-2xl border-2 border-dashed border-mono-300 px-4 py-10 text-center dark:border-mono-700">
                            <ClipboardText size={40} className="mx-auto mb-2 text-mono-400" />
                            <p className="text-base text-mono-600 dark:text-mono-400">
                                {label('wms.collector.service_orders_empty')}
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {ready.map((order) => (
                                <li
                                    key={order.id}
                                    className="space-y-3 rounded-2xl border-2 border-mono-300 bg-white p-4 dark:border-mono-700 dark:bg-mono-950"
                                >
                                    <ServiceOrderHead label={label} order={order} />
                                    <Button
                                        size="lg"
                                        className="w-full py-4 text-lg font-black"
                                        onClick={() => handleAccept(order)}
                                        loading={acceptingId === order.id}
                                        disabled={Boolean(acceptingId) && acceptingId !== order.id}
                                    >
                                        {label('wms.collector.accept')}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            <div className="sticky bottom-0 pt-2">
                <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                    <ArrowLeft size={20} className="mr-2" />
                    {label('wms.collector.back_to_menu')}
                </Button>
            </div>
        </div>
    );
};

export default ServiceOrderList;
